import { Journal, JournalTaxTypeEnum, UpdateJournalLineReq } from '../../types/journal';
import { DataGrid, DataGridColumn } from '@cimplico/components';
import { ROW_SIZES } from '../../constants';
import { useState, useEffect } from 'react';
import { useUpdateJournals } from '../../queries/use-update-journals';
import { toastNotification } from '../../components/toast/Toast';
import JournalLineRow from './JournalLineRow';
import JournalTotals from './JournalTotals';

const columns: DataGridColumn[] = [{ alignment: 'center', width: '1fr' }];

type JournalLinesTableProps = { journal: Journal };

function JournalLinesTable({ journal }: JournalLinesTableProps): JSX.Element {
    const [lines, setLines] = useState(journal.lines);

    const updateJournalQuery = useUpdateJournals(undefined, journal.id);

    function handleReorder(sortedIds: string[]): void {
        const sorted = [...lines].sort((a, b) => sortedIds.indexOf(a.id) - sortedIds.indexOf(b.id));
        setLines(sorted);
    }

    function updateOrder(): void {
        const prevLines = journal.lines.map((l) => l.id);
        const sortedLines = lines.map((l) => l.id);
        const changedLines: UpdateJournalLineReq[] = [];

        for (const line of sortedLines) {
            if (prevLines.indexOf(line) !== sortedLines.indexOf(line)) {
                changedLines.push({ id: line, fields: { sequence: sortedLines.indexOf(line) + 1 } });
            }
        }

        if (changedLines.length > 0) {
            updateJournalQuery.mutate([{ id: journal.id, lines: changedLines }], {
                onSuccess: () => {
                    toastNotification('success', 'Journal line order updated!');
                },
                onError: (e) => {
                    toastNotification('error', e.response?.data.message || 'Unable to update journal!');
                },
            });
        }
    }

    useEffect(() => {
        setLines(journal.lines);
    }, [journal.lines]);

    const showTaxRate = journal.taxType && journal.taxType !== JournalTaxTypeEnum.NO_TAX;

    return (
        <>
            <DataGrid
                columns={columns}
                rowSizes={ROW_SIZES}
                totalRows={journal.lines.length}
                isSelectable={false}
                disablePagination
                // isDraggable={canReorder}
                disableDrag={updateJournalQuery.isLoading}
                emptyMessage="No journal lines"
                onMove={handleReorder}
                onDrop={updateOrder}
            >
                {journal.lines.map((jl) => {
                    return <JournalLineRow id={jl.id} key={jl.id} journalLine={jl} taxType={journal.taxType} />;
                })}
            </DataGrid>
            {journal.lines.length > 0 && (
                <JournalTotals
                    taxType={journal.taxType || JournalTaxTypeEnum.NO_TAX}
                    showTaxRate={showTaxRate}
                    values={{
                        debitTotal: journal.total?.debit,
                        creditTotal: journal.total?.credit,
                        debitSubtotal: journal.subtotal?.debit,
                        creditSubtotal: journal.subtotal?.credit,
                        debitTaxTotal: journal.taxTotal?.debit,
                        creditTaxTotal: journal.taxTotal?.credit,
                    }}
                />
            )}
        </>
    );
}

export default JournalLinesTable;
